import React, {useEffect, useMemo, useState} from "react";
import JSZip from "jszip";
import {saveAs} from "file-saver";
import Navigation from "../Components/Navigation";
import API from "../api/config.js";
import QRCode from "qrcode";
import toast from "react-hot-toast";

// ✅ Add these lines here
const ORIGIN =
    typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITE_APP_ORIGIN
        ? import.meta.env.VITE_APP_ORIGIN
        : window.location.origin;

const dataURLtoBlob = (dataUrl) => {
    const [meta, b64] = dataUrl.split(",");
    const mime = meta.match(/data:(.*?);base64/)[1] || "image/png";
    const bin = atob(b64),
        len = bin.length,
        u8 = new Uint8Array(len);
    for (let i = 0; i < len; i++) u8[i] = bin.charCodeAt(i);
    return new Blob([u8], {type: mime});
};

// --- icons ---
const EyeIcon = () => (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor">
        <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z" strokeWidth="2" />
        <circle cx="12" cy="12" r="3" strokeWidth="2" />
    </svg>
);
const TrashIcon = () => (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor">
        <path
            d="M4 7h16M10 11v6m4-6v6M9 7V5h6v2m-8 0 1 12h8l1-12"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const DownloadIcon = () => (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor">
        <path d="M12 3v12m0 0 4-4m-4 4-4-4M4 21h16" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// --- helpers ---
const genProjectId = () => {
    const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let s = "";
    for (let i = 0; i < 9; i++) s += alphabet[Math.floor(Math.random() * alphabet.length)];
    return `PROJECT-${s}`;
};

const ActionBtn = ({title, onClick, children}) => (
    <button
        type="button"
        title={title}
        onClick={onClick}
        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
    >
        {children}
    </button>
);

// --- modal ---
const Modal = ({open, onClose, onSubmit, initial}) => {
    const [form, setForm] = useState(
        initial || {
            name: "",
            customer: "",
            User: "",
            totalQr: "",
            description: "",
        }
    );

    useEffect(() => {
        if (open) {
            setForm(initial || {name: "", customer: "", User: "", totalQr: "", description: ""});
        }
    }, [open, initial]);

    const handleChange = (e) => setForm((f) => ({...f, [e.target.name]: e.target.value}));

    const submit = (e) => {
        e.preventDefault();
        if (!form.name || !form.customer || !form.totalQr) {
            toast.error("Name of Project, Customer, and Total QR are required.");
            return;
        }
        onSubmit({
            projectId: genProjectId(),
            name: form.name,
            customer: form.customer,
            User: form.User || "—",
            totalQr: Number(form.totalQr),
            description: form.description || "",
            createdAt: new Date().toISOString(),
        });
        onClose();
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-2xl rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800">
                <div className="flex items-center justify-between border-b border-gray-200 p-5 dark:border-gray-700">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Add Projects</h3>
                    <button className="text-gray-500 hover:text-gray-800 dark:text-gray-300" onClick={onClose}>
                        ✕
                    </button>
                </div>

                <form onSubmit={submit} className="p-5 space-y-4">
                    {/* Name */}
                    <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Name of Project
                        </label>
                        <input
                            name="name"
                            placeholder="Enter Name"
                            value={form.name}
                            onChange={handleChange}
                            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:border-gray-400 focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                        />
                    </div>

                    {/* Customer / User */}
                    <div className="grid gap-4 md:grid-cols-2">
                        <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Customer
                            </label>
                            <input
                                name="customer"
                                placeholder="Select a Customer"
                                value={form.customer}
                                onChange={handleChange}
                                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:border-gray-400 focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                User
                            </label>
                            <input
                                name="User"
                                placeholder="Select a User"
                                value={form.User}
                                onChange={handleChange}
                                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:border-gray-400 focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                            />
                        </div>
                    </div>

                    {/* Total QR */}
                    <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Total QR to create
                        </label>
                        <input
                            type="number"
                            min={1}
                            name="totalQr"
                            placeholder="Total QR"
                            value={form.totalQr}
                            onChange={handleChange}
                            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:border-gray-400 focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Description
                        </label>
                        <textarea
                            name="description"
                            rows={3}
                            placeholder="Description"
                            value={form.description}
                            onChange={handleChange}
                            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:border-gray-400 focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                        />
                    </div>

                    <div className="flex items-center justify-between pt-1">
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-xl bg-gray-200 px-4 py-2.5 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
                        >
                            Back
                        </button>
                        <button
                            type="submit"
                            className="rounded-xl bg-rose-500 px-4 py-2.5 font-medium text-white hover:bg-rose-600"
                        >
                            Submit
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const Create = () => {
    const [theme, setTheme] = useState(() => localStorage.getItem("travelxoxo-theme") || "light");
    const [activeScreen, setActiveScreen] = useState("create");
    const [openModal, setOpenModal] = useState(false);
    const [rows, setRows] = useState([]);
    const [search, setSearch] = useState("");
    const [pageSize, setPageSize] = useState(100);
    const [downloadingProject, setDownloadingProject] = useState(null);

    const [viewOpen, setViewOpen] = useState(false);
    const [viewProject, setViewProject] = useState(null);

    const toUI = (r) => ({
        id: r.id,
        projectId: r.project_id,
        name: r.name,
        customer: r.customer,
        User: r.user_name,
        totalQr: r.total_qr,
        description: r.description || "",
        createdAt: r.created_at,
    });

    useEffect(() => {
        document.documentElement.classList.toggle("dark", theme === "dark");
        localStorage.setItem("travelxoxo-theme", theme);
    }, [theme]);

    const toggleTheme = () => setTheme((t) => (t === "light" ? "dark" : "light"));

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return rows
        .filter(
            (r) =>
                r.projectId.toLowerCase().includes(q) ||
                r.name.toLowerCase().includes(q) ||
                r.customer.toLowerCase().includes(q) ||
                (r.User || "").toLowerCase().includes(q)
        )
        .slice(0, pageSize);
    }, [rows, search, pageSize]);

    // --- API calls ---
    const load = async () => {
        try {
            const {data} = await API.get("/projects");
            setRows(data.map(toUI));
        } catch (err) {
            console.error(err);
            toast.error("Error fetching projects");
        }
    };

    const addRow = async (entry) => {
        try {
            await API.post("/projects", {
                name: entry.name,
                customer: entry.customer,
                user_name: entry.User || "system", // map UI → backend
                total_qr: entry.totalQr, // camelCase → snake_case
                description: entry.description || null,
            });
            await load();
        } catch (err) {
            console.error("Error creating project:", err);
            const msg = err?.response?.data?.message || err.message || "Operation failed";

            toast.error(msg);
        }
    };

    const delRow = async (id) => {
        try {
            await API.delete(`/projects/${id}`);
            setRows((prev) => prev.filter((r) => r.id !== id));
        } catch (err) {
            console.error(err);
            toast.error("Error deleting project");
        }
    };

    const viewRow = (r) => {
        setViewProject(r);
        setViewOpen(true);
    };

    useEffect(() => {
        load();
    }, []);

    const exportCSV = () => {
        if (!rows.length) return;
        const headers = [
            "S No",
            "Project Id",
            "Name Of Project",
            "Customer",
            "Customer User",
            "Total Qr",
            "Description",
        ];
        const data = rows.map((r, i) => [
            i + 1,
            r.projectId,
            r.name,
            r.customer,
            r.User || "",
            r.totalQr,
            (r.description || "").replace(/\n/g, " "),
        ]);
        const csv = [headers, ...data]
        .map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
        .join("\n");
        const blob = new Blob([csv], {type: "text/csv;charset=utf-8;"});
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "qr-projects.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleDownloadProject = async (projectId) => {
        if (downloadingProject) return;
        setDownloadingProject(projectId);

        try {
            console.time("zip-qrs");

            // 1) fetch all codes for project
            const {data} = await API.get(`/qrcodes/project/${projectId}`, {
                timeout: 120000,
            });

            if (!Array.isArray(data) || data.length === 0) {
                toast.error("No QR codes found for this project.");
                return;
            }

            // 2) build zip
            const zip = new JSZip();
            const folder = zip.folder(projectId) || zip;

            // concurrency limiter
            const limit = 8;
            let idx = 0;
            let ok = 0;
            let fail = 0;

            const jobs = data.map((row) => async () => {
                try {
                    const value = `${ORIGIN}/${row.qr_id}`;
                    const dataUrl = await QRCode.toDataURL(value, {
                        errorCorrectionLevel: "H",
                        width: 1024,
                        margin: 1,
                        color: {dark: "#000000", light: "#FFFFFF"},
                    });

                    folder.file(`${row.qr_id}.png`, dataURLtoBlob(dataUrl));
                    ok++;
                } catch {
                    fail++;
                }
            });

            const runners = new Array(Math.min(limit, jobs.length)).fill(0).map(async () => {
                while (idx < jobs.length) {
                    const i = idx++;
                    await jobs[i]();
                }
            });

            await Promise.all(runners);

            // CSV manifest (ONLY 3 COLUMNS)
            const header = "qrurl,project_id,project_name";

            const lines = data.map((r) => {
                const qrurl = `${ORIGIN}/${r.qr_id}`;
                return [qrurl, r.project_id || projectId, r.project_name || ""]
                .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
                .join(",");
            });

            zip.file(`${projectId}.csv`, [header, ...lines].join("\n"));

            // 3) save
            const blob = await zip.generateAsync({
                type: "blob",
                compression: "DEFLATE",
                compressionOptions: {level: 6},
            });

            saveAs(blob, `${projectId}-qrcodes.zip`);
            console.timeEnd("zip-qrs");

            if (fail) toast.error(`Completed: ${ok} OK, ${fail} failed.`);
        } catch (err) {
            console.error("Download error:", err);
            toast.error(`Failed to download QR codes. ${err?.response?.data?.message || err.message || ""}`);
        } finally {
            setDownloadingProject(null);
        }
    };
    return (
        <div className="flex bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-300">
            <Navigation
                activeScreen={activeScreen}
                setActiveScreen={setActiveScreen}
                theme={theme}
                toggleTheme={toggleTheme}
            />
            <main className="flex-1 ml-20 md:ml-64 p-6 md:p-10">
                {/* Header */}
                <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Projects</h1>

                    <button
                        onClick={exportCSV}
                        className="rounded-xl bg-pink-600 hover:bg-pink-700 px-4 py-2.5 text-white shadow-sm hover:bg-orange-700"
                        title="Export Excel"
                    >
                        Export
                    </button>
                    <button
                        onClick={() => setOpenModal(true)}
                        className="rounded-xl bg-pink-600 hover:bg-pink-700 px-4 py-2.5 text-white shadow-sm hover:bg-orange-700"
                    >
                        Add Qr-codes
                    </button>
                </div>

                {/* Toolbar */}
                <div className="mb-3 flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        Show
                        <select
                            value={pageSize}
                            onChange={(e) => setPageSize(Number(e.target.value))}
                            className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                        >
                            {[10, 25, 50, 100].map((n) => (
                                <option key={n} value={n}>
                                    {n}
                                </option>
                            ))}
                        </select>
                        entries
                    </label>

                    <div className="ml-auto flex items-center gap-2"></div>

                    <div>
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search:"
                            className="w-56 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:border-gray-400 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                        />
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-left text-sm">
                            <thead className="bg-gray-50 text-gray-600 dark:bg-gray-900/40 dark:text-gray-300">
                                <tr>
                                    <th className="px-5 py-3 font-semibold">S No</th>
                                    <th className="px-5 py-3 font-semibold">Project Id</th>
                                    <th className="px-5 py-3 font-semibold">Name</th>
                                    <th className="px-5 py-3 font-semibold">Customer</th>
                                    <th className="px-5 py-3 font-semibold">User</th>
                                    <th className="px-5 py-3 font-semibold">Total Qr</th>
                                    <th className="px-5 py-3 font-semibold">Description</th>
                                    <th className="px-5 py-3 font-semibold">Options</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={8}
                                            className="px-6 py-10 text-center text-gray-500 dark:text-gray-400"
                                        >
                                            No projects yet. Click <span className="font-semibold">Add Qr-codes</span>{" "}
                                            to create one.
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.map((r, idx) => (
                                        <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                                            <td className="px-5 py-3 text-gray-900 dark:text-gray-100">{idx + 1}</td>
                                            <td className="px-5 py-3 font-mono text-gray-900 dark:text-gray-100">
                                                {r.projectId}
                                            </td>
                                            <td className="px-5 py-3 text-gray-900 dark:text-gray-100">{r.name}</td>
                                            <td className="px-5 py-3 text-gray-900 dark:text-gray-100">{r.customer}</td>
                                            <td className="px-5 py-3 text-gray-900 dark:text-gray-100">{r.User}</td>
                                            <td className="px-5 py-3 text-gray-900 dark:text-gray-100">{r.totalQr}</td>
                                            <td className="px-5 py-3 text-gray-900 dark:text-gray-100">
                                                {r.description}
                                            </td>

                                            <td className="px-5 py-3">
                                                <div className="flex items-center gap-2">
                                                    {/* View */}
                                                    <ActionBtn title="View" onClick={() => viewRow(r)}>
                                                        <EyeIcon />
                                                    </ActionBtn>

                                                    {/* Download */}
                                                    <ActionBtn
                                                        title={
                                                            downloadingProject === r.projectId
                                                                ? "Downloading…"
                                                                : "Download"
                                                        }
                                                        onClick={() => handleDownloadProject(r.projectId)}
                                                    >
                                                        <DownloadIcon />
                                                    </ActionBtn>

                                                    {/* Delete */}
                                                    <ActionBtn title="Delete" onClick={() => delRow(r.id)}>
                                                        <TrashIcon />
                                                    </ActionBtn>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Modal */}
                <Modal open={openModal} onClose={() => setOpenModal(false)} onSubmit={addRow} />

                {/* View modal */}
                {viewOpen && viewProject && (
                    <div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                        onClick={() => setViewOpen(false)}
                    >
                        <div
                            className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-800"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Project Details</h3>
                                <button className="text-gray-500 dark:text-gray-300" onClick={() => setViewOpen(false)}>
                                    ✕
                                </button>
                            </div>

                            <div className="space-y-2 text-gray-800 dark:text-gray-200">
                                <p>
                                    <span className="font-semibold">Project:</span> {viewProject.name}
                                </p>
                                <p>
                                    <span className="font-semibold">Project ID:</span> {viewProject.projectId}
                                </p>
                                <p>
                                    <span className="font-semibold">Customer:</span> {viewProject.customer}
                                </p>
                                <p>
                                    <span className="font-semibold">User:</span> {viewProject.User || "—"}
                                </p>
                                <p>
                                    <span className="font-semibold">Total QR:</span> {viewProject.totalQr}
                                </p>
                                <p>
                                    <span className="font-semibold">Description:</span> {viewProject.description || "—"}
                                </p>
                            </div>

                            <div className="mt-6 flex justify-end">
                                <button
                                    onClick={() => setViewOpen(false)}
                                    className="rounded-xl bg-gray-200 px-4 py-2 dark:bg-gray-700 dark:text-gray-100"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default Create;
